import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import { SCHEDULE_YEAR } from './constants.js'
import { ArrowRightIcon, CalendarDaysIcon } from '@heroicons/react/20/solid'
import DocumentMeta from './DocumentMeta.jsx'
import { trackEvent } from './lib/analytics'
import { reportError } from './lib/reportError.jsx'
import { supabase } from '../lib/supabase'

const SUBJECT_CATEGORIES = {
  language: ['1ο πεδίο', '2ο πεδίο', '3ο πεδίο', '4ο πεδίο'],
  ancient: ['1ο πεδίο'],
  latin: ['1ο πεδίο'],
  history: ['1ο πεδίο'],
  math: ['2ο πεδίο'],
  physics: ['2ο πεδίο'],
  chemistry: ['3ο πεδίο'],
  biology: ['3ο πεδίο'],
  informatics: ['4ο πεδίο'],
  economics: ['4ο πεδίο'],
}

function formatScheduleEntry(entry, subjectNames) {
  const startsAt = new Date(entry.starts_at)
  const date = new Intl.DateTimeFormat('el-GR', {
    timeZone: 'Europe/Athens',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(startsAt)
  const day = new Intl.DateTimeFormat('el-GR', {
    timeZone: 'Europe/Athens',
    weekday: 'long',
  }).format(startsAt)

  return {
    ...entry,
    date,
    day: day.charAt(0).toUpperCase() + day.slice(1),
    time: new Intl.DateTimeFormat('el-GR', {
      timeZone: 'Europe/Athens',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(startsAt),
    subject: {
      slug: entry.subject_slug,
      name: subjectNames.get(entry.subject_slug) || entry.subject_slug,
    },
  }
}

function groupSchedule(entries, subjectNames) {
  const grouped = new Map()

  entries.map((entry) => formatScheduleEntry(entry, subjectNames)).forEach((entry) => {
    const key = `${entry.kind}-${entry.starts_at}`
    const group = grouped.get(key) || {
      type: entry.kind,
      startsAt: entry.starts_at,
      day: entry.day,
      date: entry.date,
      time: entry.time,
      subjects: [],
      categories: [],
    }
    group.subjects.push(entry.subject)
    group.categories.push(...(SUBJECT_CATEGORIES[entry.subject.slug] || ['Ειδικά μαθήματα']))
    grouped.set(key, group)
  })

  return Array.from(grouped.values()).map((group) => ({
    ...group,
    categories: [...new Set(group.categories)],
  }))
}

function CategoriesCompact({ categories }) {
  const MAX = 2
  const shown = categories.slice(0, MAX)
  const rest = categories.length - shown.length

  return (
    <div className="flex flex-wrap gap-2">
      {shown.map((c, i) => (
        <span
          key={i}
          className="inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border border-gray-200 bg-white text-gray-700"
          title={c}
        >
          {c}
        </span>
      ))}
      {rest > 0 ? (
        <span className="inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-600">
          +{rest}
        </span>
      ) : null}
    </div>
  )
}

function SubjectChip({ subject, onClick, disabled }) {
  if (disabled) {
    return (
      <span
        className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-semibold text-gray-500"
        title={subject.name}
      >
        <span className="truncate">{subject.name}</span>
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 transition"
      title="Δες θέματα"
    >
      <span className="truncate">{subject.name}</span>
      <ArrowRightIcon className="w-4 h-4 text-gray-500" />
    </button>
  )
}

function ExamRow({ exam, onSubjectClick }) {
  const isSpecial = exam.type === 'special'
  const isSingle = exam.subjects.length === 1

  const slug = isSingle ? exam.subjects[0].slug : null
  const clickable = isSingle && !!slug && slug !== 'music_performance' && slug !== 'music_theory'


  const handleRowClick = () => {
    if (!clickable) return
    onSubjectClick(exam.subjects[0].slug)
  }

  return (
    <div
      onClick={handleRowClick}
      className={[
        'group relative overflow-hidden rounded-2xl border shadow-sm',
        'px-4 py-4 sm:px-5 sm:py-5',
        'transition-shadow hover:shadow-md',
        clickable ? 'cursor-pointer' : '',
        isSpecial ? 'border-gray-300 bg-gray-50' : 'border-gray-200 bg-white',
      ].join(' ')}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (!clickable) return
        if (e.key === 'Enter' || e.key === ' ') handleRowClick()
      }}
    >
      {/* subtle diagonal lines for special rows */}
      {isSpecial ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(135deg, #111827 0px, #111827 1px, transparent 1px, transparent 16px)',
          }}
        />
      ) : null}

      {/* hover hint (only when row is clickable) */}
      {clickable ? (
        <div className="pointer-events-none absolute right-4 top-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-900 text-white text-[11px] font-semibold px-2.5 py-1">
            Δες θέματα
            <ArrowRightIcon className="w-4 h-4" />
          </span>
        </div>
      ) : null}

      <div className="relative grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-start">
        {/* Left: date/day/time */}
        <div className="sm:col-span-3">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-base font-extrabold text-gray-900">{exam.day}</p>
            {isSpecial ? (
              <span className="text-[11px] font-semibold text-gray-600 bg-gray-200/70 px-2 py-0.5 rounded-full">
                Ειδικά
              </span>
            ) : null}
          </div>

          <p className="text-sm text-gray-600 mt-0.5">
            {exam.date}
            <span className="text-gray-400"> • </span>
            <span className="font-semibold text-gray-800">{exam.time}</span>
          </p>
        </div>

        {/* Middle: subjects */}
        <div className="sm:col-span-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Μαθήματα
          </p>

          {isSingle ? (
            <p className="mt-1 text-sm sm:text-[15px] leading-snug text-gray-900 font-semibold">
              {exam.subjects[0].name}
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {exam.subjects.map((subject) => {
                const slug = subject.slug
                const isMusicPerformance = slug === 'music_performance'

                return (
                  <SubjectChip
                    key={slug}
                    subject={subject}
                    disabled={isMusicPerformance}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (isMusicPerformance) return
                      onSubjectClick(slug)
                    }}
                  />
                )
              })}
            </div>
          )}

          {!isSingle ? (
            <p className="mt-2 text-xs text-gray-500">
              Πάτησε σε μάθημα για να δεις θέματα
            </p>
          ) : null}
        </div>

        {/* Right: categories compact */}
        <div className="sm:col-span-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {isSpecial ? 'Κατηγορία' : 'Ομάδες'}
          </p>
          <div className="mt-2">
            <CategoriesCompact categories={exam.categories} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ExamSchedulePage() {
  const { year } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const scheduleYear = year ? Number(year) : NaN

  const shouldRedirect = !year || Number.isNaN(scheduleYear) || scheduleYear !== SCHEDULE_YEAR

  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/programma/${SCHEDULE_YEAR}`, { replace: true })
    }
  }, [shouldRedirect, navigate])

  const [showSpecial, setShowSpecial] = useState(false)
  const [schedule, setSchedule] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (shouldRedirect) return

    let active = true

    async function loadSchedule() {
      setLoading(true)
      setLoadError(false)

      const [{ data: entries, error: entriesError }, { data: subjects, error: subjectsError }] = await Promise.all([
        supabase
          .from('exam_schedule')
          .select('id, exam_year, subject_slug, starts_at, kind')
          .eq('exam_year', scheduleYear)
          .order('starts_at'),
        supabase.from('subjects').select('slug, name'),
      ])

      if (!active) return

      if (entriesError || subjectsError) {
        const error = entriesError || subjectsError
        console.error('Error loading exam schedule', error)
        reportError(error, { where: 'ExamSchedulePage.loadSchedule', scheduleYear })
        setLoadError(true)
        setSchedule([])
      } else {
        const subjectNames = new Map((subjects || []).map((subject) => [subject.slug, subject.name]))
        setSchedule(groupSchedule(entries || [], subjectNames))
      }

      setLoading(false)
    }

    loadSchedule()
    return () => { active = false }
  }, [scheduleYear, shouldRedirect])

  const primaryExamSchedule = useMemo(
    () => schedule.filter((exam) => exam.type === 'primary'),
    [schedule],
  )
  const specialExamSchedule = useMemo(
    () => schedule.filter((exam) => exam.type === 'special'),
    [schedule],
  )
  const hasSchedule = schedule.length > 0

  const examsToRender = useMemo(() => {
    const base = [...primaryExamSchedule]
    if (showSpecial) base.push(...specialExamSchedule)
    return base.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
  }, [primaryExamSchedule, showSpecial, specialExamSchedule])

  const onSubjectClick = (slug) => {
    if (!slug) return

    trackEvent('schedule_subject_clicked', {
      schedule_year: scheduleYear,
      subject_slug: slug,
      schedule_type: specialExamSchedule.some((exam) => exam.subjects.some((subject) => subject.slug === slug))
        ? 'special'
        : 'primary',
    })

    navigate(`/themata/ola?subject=${encodeURIComponent(slug)}`)
  }

  if (shouldRedirect) return null

  return (
    <>
      <DocumentMeta
        title={`Πρόγραμμα Πανελληνίων ${SCHEDULE_YEAR} | Ημερομηνίες Εξετάσεων`}
        description={`Δες το επίσημο πρόγραμμα Πανελληνίων ${SCHEDULE_YEAR} με ημερομηνίες και ώρες για όλα τα μαθήματα.`}
        canonical={`https://www.panellhniesplus.gr${location.pathname}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Αρχική", item: "https://www.panellhniesplus.gr/" },
          { "@type": "ListItem", position: 2, name: `Πρόγραμμα Πανελληνίων ${SCHEDULE_YEAR}`, item: `https://www.panellhniesplus.gr${location.pathname}` },
        ],
      }) }} />

      <div className="min-h-screen bg-white text-gray-900">
        <Header />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 sm:py-32 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gray-100 mb-6">
                <CalendarDaysIcon className="w-10 h-10 text-gray-400 animate-pulse" />
              </div>
              <p className="text-sm text-gray-500">Φορτώνουμε το πρόγραμμα εξετάσεων…</p>
            </div>
          ) : hasSchedule ? (
            <>
              <header className="text-center mb-6 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
                  Πρόγραμμα Πανελληνίων {SCHEDULE_YEAR}
                </h1>
                <p className="text-sm text-gray-500 mt-2">
                  Δες τις ημερομηνίες και τα μαθήματα ανά ημέρα.
                </p>

                <div className="mt-5 flex items-center justify-center">
                  <label className="inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2 shadow-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-gray-900 cursor-pointer"
                      checked={showSpecial}
                      onChange={(e) => setShowSpecial(e.target.checked)}
                    />
                    <span className="text-sm font-semibold text-gray-800">
                      Εμφάνιση ειδικών μαθημάτων
                    </span>
                    <span className="text-xs text-gray-500 hidden sm:inline">
                      (Αγγλικά, Σχέδιο, Μουσικά, κλπ)
                    </span>
                  </label>
                </div>
              </header>

              <section className="space-y-3 sm:space-y-4">
                {examsToRender.map((exam, idx) => (
                  <ExamRow
                    key={`${exam.type}-${exam.startsAt}-${idx}`}
                    exam={exam}
                    onSubjectClick={onSubjectClick}
                  />
                ))}
              </section>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 sm:py-32 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gray-100 mb-6">
                <CalendarDaysIcon className="w-10 h-10 text-gray-400" />
              </div>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500 tracking-wide uppercase mb-4">
                {loadError ? 'Προσωρινό πρόβλημα' : 'Σύντομα διαθέσιμο'}
              </span>

              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 mb-3">
                Πρόγραμμα Πανελληνίων {SCHEDULE_YEAR}
              </h1>

              <p className="text-sm sm:text-base text-gray-500 max-w-sm leading-relaxed">
                {loadError
                  ? 'Δεν ήταν δυνατή η φόρτωση του προγράμματος. Δοκίμασε ξανά σε λίγο.'
                  : 'Το πρόγραμμα εξετάσεων δεν έχει ανακοινωθεί ακόμα από το Υπουργείο Παιδείας. Μόλις γίνει διαθέσιμο, θα το βρεις εδώ.'}
              </p>

              <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 w-full max-w-xs sm:max-w-sm opacity-30 pointer-events-none select-none" aria-hidden="true">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-10 rounded-xl bg-gray-200 animate-pulse" style={{ animationDelay: `${i * 120}ms` }} />
                ))}
              </div>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  )
}
